/**
 * Slack OAuth コールバック中継ページ
 *
 * Amplify Auth が URL の ?code= を Cognito OAuth と誤認するため、
 * このページは認証フロー（PrivateRoute）の外に配置する。
 * code/state を sessionStorage に退避し、/settings/tenant にリダイレクトする。
 * 実際の API 呼び出しは TenantPage コンテナで行う。
 *
 * @return {JSX.Element} リダイレクト中表示
 */
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const SlackOAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const savedState = sessionStorage.getItem("slack_oauth_state");

    // state 検証（CSRF防止）
    if (!state || state !== savedState) {
      setError("不正なリクエストです（state不一致）。もう一度お試しください。");
      return;
    }

    if (!code) {
      const slackError = searchParams.get("error");
      if (slackError === "access_denied") {
        // ユーザーがキャンセルした場合
        sessionStorage.removeItem("slack_oauth_state");
        window.location.href = "/settings/tenant";
        return;
      }
      setError("認可コードが取得できませんでした。");
      return;
    }

    // code を sessionStorage に退避してリダイレクト
    // （state は TenantPage 側で検証済みとして削除）
    sessionStorage.removeItem("slack_oauth_state");
    sessionStorage.setItem("slack_oauth_code", code);

    // window.location.href で完全なページ遷移（Amplify の code 誤認を回避）
    window.location.href = "/settings/tenant";
  }, [searchParams]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen space-y-4">
        <div className="alert alert-error max-w-md">
          <span>{error}</span>
        </div>
        <a href="/settings/tenant" className="btn btn-ghost btn-sm">
          テナント管理に戻る
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen space-y-4">
      <span className="loading loading-spinner loading-lg" />
      <p className="text-base-content/60">Slack連携を処理中...</p>
    </div>
  );
};

export default SlackOAuthCallbackPage;
